import { observer } from 'mobx-react';
import React from 'react';
import {
    Navigate,
    PartialRouteObject,
    Route as RouterRoute,
    useLocation,
    useRoutes
} from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { SidebarInset, SidebarProvider } from '@components/ui/sidebar';
import { Toaster } from '@components/ui/sonner';
import { TooltipProvider } from '@components/ui/tooltip';
import { BrowserTitle } from '../components/browsertitle';
import { Spinner } from '../components/spinner';
import { Api } from '../core/api';
import { Route as RouteType, Route, Routes } from '../core/routes';
import { injectTSDI } from '../core/tsdi';
import { NoMatch } from '../no-match';
import { ErrorBoundary } from './error-boundary';
import { Header } from './header';
import { AppSidebar } from './sidebar';

const privateRoute = (route: Route) =>
    Boolean(route.permissions?.includes('LOGGED_IN'));

const showRoute = (privatePath: boolean, loggedIn: boolean) =>
    !loggedIn && privatePath ? true : false;

const generateRoutes = (
    routes: RouteType[],
    pathname: string,
    parent?: Route
): PartialRouteObject[] => {
    const { loggedIn } = injectTSDI(Api);

    const isLogin = pathname === '/login';

    return routes.map((route) => {
        const redirect =
            !isLogin &&
            showRoute(privateRoute(parent ? parent : route), loggedIn);

        const { element: Element, children, ...rest } = route;
        return {
            ...rest,
            ...(children
                ? { children: generateRoutes(children, pathname, route) }
                : {}),
            element: redirect ? <Navigate to="/login" /> : Element
        };
    });
};

const RouteContent = observer(() => {
    const { routes } = injectTSDI(Routes);
    const { pathname } = useLocation();

    return useRoutes([...generateRoutes(routes, pathname)]);
});

export const App = observer(() => {
    const { loggedIn, googleAuthChecked, googleClientId } = injectTSDI(Api);

    return (
        <ErrorBoundary>
            <AuthCheck />
            <TooltipProvider delayDuration={200}>
                <Toaster
                    position="top-right"
                    visibleToasts={3}
                    closeButton
                    richColors
                />
                <GoogleOAuthProvider clientId={googleClientId}>
                    {googleAuthChecked ? (
                        <BrowserRouter>
                            <BrowserTitle />
                            {loggedIn ? (
                                <SidebarProvider>
                                    <AppSidebar />
                                    <SidebarInset className="min-w-0">
                                        <Header />
                                        <div className="mx-auto w-full max-w-[1400px] p-6">
                                            <RouteContent />
                                        </div>
                                    </SidebarInset>
                                </SidebarProvider>
                            ) : (
                                <main className="min-h-screen">
                                    <RouteContent />
                                </main>
                            )}
                            <RouterRoute path="*">
                                <NoMatch />
                            </RouterRoute>
                        </BrowserRouter>
                    ) : (
                        <div className="flex h-screen w-full items-center justify-center">
                            <Spinner size="lg" />
                        </div>
                    )}
                </GoogleOAuthProvider>
            </TooltipProvider>
        </ErrorBoundary>
    );
});

const AuthCheck = observer(() => {
    const { checkForPermissions } = injectTSDI(Api);

    React.useEffect(() => {
        checkForPermissions();
    }, [checkForPermissions]);
    return null;
});
